import { defineComponent as _defineComponent } from 'vue'
import { SnowplowOldModuleContext, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { GlobalModuleName, TitleType } from '@/helpers/composables/useGlobalTitles';

import { computed } from 'vue';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';

import { ObjectType } from '@/@types/graphql-types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useI18n } from '@/helpers/composables/useI18n';
import { useGlobalTitles } from '@/helpers/composables/useGlobalTitles';
import { ImpressionTrackingEvents } from '@/enums/events';
import { defaultObservableOptions } from '@/mixins/types/impressionTracking';
import { useConstantStore } from '@/helpers/composables/useStores';

export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalTitles',
  props: {
    country: null,
    language: null,
    objectType: null,
    genre: null,
    type: null,
    moduleName: null
  },
  setup(__props: any) {

const props = __props as {
	country: string;
	language: string;
	objectType?: ObjectType.Movie | ObjectType.Show;
	genre?: 'drm' | 'cmy' | 'hrr';
	type: TitleType;
	moduleName: GlobalModuleName;
};



const { tc, t } = useI18n();

const { titles } = useGlobalTitles(props);

const { genresByShortName } = useConstantStore();

const genreFullName = computed(() => {
	if (!props.genre) return '';
	return genresByShortName.value[props.genre].translation;
});

const title = computed(() => {
	if (props.genre) {
		return t('WEBAPP_GLOBAL_TOP_10_GENRE', { genre: genreFullName.value });
	}

	if (props.moduleName === 'MAIN_JWTV_TVOD') {
		return tc('WEBAPP_GLOBAL_JWTV_TVOD');
	}

	if (props.moduleName === 'MAIN_JWTV_AVOD') {
		return tc('WEBAPP_GLOBAL_FREE_TITLES');
	}

	if (props.objectType === ObjectType.Movie) {
		return {
			TOP_10: tc('WEBAPP_GLOBAL_TOP_10_MOVIE'),
			UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_MOVIE'),
			NEW: tc('WEBAPP_GLOBAL_NEW_MOVIE'),
			POPULAR: tc('WEBAPP_GLOBAL_POPULAR_MOVIE'),
		}[props.type];
	}

	return {
		TOP_10: tc('WEBAPP_GLOBAL_TOP_10_SHOW'),
		UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_SHOW'),
		NEW: tc('WEBAPP_GLOBAL_NEW_SHOW'),
		POPULAR: tc('WEBAPP_GLOBAL_POPULAR_SHOW'),
	}[props.type];
});

const text = computed(() => {
	if (props.genre) {
		return t('WEBAPP_GLOBAL_TOP_10_GENRE_TEXT', { genre: genreFullName.value });
	}

	if (props.moduleName === 'MAIN_JWTV_TVOD') {
		return tc('WEBAPP_GLOBAL_JWTV_TVOD_TEXT');
	}
	if (props.moduleName === 'MAIN_JWTV_AVOD') {
		return tc('WEBAPP_GLOBAL_FREE_TITLES_TEXT');
	}

	if (props.objectType === ObjectType.Movie) {
		return {
			TOP_10: tc('WEBAPP_GLOBAL_TOP_10_MOVIE_TEXT'),
			UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_MOVIE_TEXT'),
			NEW: tc('WEBAPP_GLOBAL_NEW_MOVIE_TEXT'),
			POPULAR: tc('WEBAPP_GLOBAL_POPULAR_MOVIE_TEXT'),
		}[props.type];
	}

	return {
		TOP_10: tc('WEBAPP_GLOBAL_TOP_10_SHOW_TEXT'),
		UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_SHOW_TEXT'),
		NEW: tc('WEBAPP_GLOBAL_NEW_SHOW_TEXT'),
		POPULAR: tc('WEBAPP_GLOBAL_POPULAR_SHOW_TEXT'),
	}[props.type];
});

const linkText = computed(() => {
	if (props.moduleName === 'MAIN_JWTV_AVOD') {
		return null;
	}
	if (props.objectType === ObjectType.Movie) {
		return {
			TOP_10: null,
			UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_MOVIE_SEE_ALL'),
			NEW: tc('WEBAPP_GLOBAL_NEW_MOVIE_SEE_ALL'),
			POPULAR: tc('WEBAPP_GLOBAL_POPULAR_MOVIE_SEE_ALL'),
		}[props.type];
	}

	return {
		TOP_10: null,
		UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_SHOW_SEE_ALL'),
		NEW: tc('WEBAPP_GLOBAL_NEW_SHOW_SEE_ALL'),
		POPULAR: tc('WEBAPP_GLOBAL_POPULAR_SHOW_SEE_ALL'),
	}[props.type];
});

const link = computed(() => {
	if (props.objectType === ObjectType.Movie) {
		if (props.type === 'TOP_10') return null;
		if (props.type === 'POPULAR') return 'app.titles.popular.movie';
		if (props.type === 'NEW') return 'app.titles.new.movie';
		if (props.type === 'UPCOMING') return 'app.titles.upcoming.movie';
	}

	if (props.objectType === ObjectType.Show) {
		if (props.type === 'TOP_10') return null;
		if (props.type === 'POPULAR') return 'app.titles.popular.show';
		if (props.type === 'NEW') return 'app.titles.new.show';
		if (props.type === 'UPCOMING') return 'app.titles.upcoming.show';
	}

	return '';
});

const trackingLabel = computed(() => {
	if (props.objectType === ObjectType.Movie) {
		if (props.type === 'TOP_10') return 'top_10_movies';
		if (props.type === 'POPULAR') return 'popular_movies';
		if (props.type === 'NEW') return 'new_movies';
		if (props.type === 'UPCOMING') return 'upcoming_movies';
	}

	if (props.objectType === ObjectType.Show) {
		if (props.type === 'TOP_10') return 'top_10_shows';
		if (props.type === 'POPULAR') return 'popular_shows';
		if (props.type === 'NEW') return 'new_shows';
		if (props.type === 'UPCOMING') return 'upcoming_shows';
	}

	return '';
});

const moduleContext = computed(() => new SnowplowOldModuleContext('', -1, props.moduleName, ''));

function handlePosterClick(title: any, index: number, contexts: SnowplowContext[] = []) {
	const titleContext = TrackingHelper.getTitleContextGraphql(
		title.node.objectId,
		title.node.objectType,
		title?.node?.content?.seasonNumber
	);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: trackingLabel.value,
			value: index,
		},
		[...contexts, titleContext, moduleContext.value]
	);
}

function handleSeeAllClick() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'content_click',
			label: trackingLabel.value,
		},
		[moduleContext.value]
	);
}

const observeVisibilityOptions = computed(() => ({
	...defaultObservableOptions,
	callback: (isVisible: boolean) => {
		if (!isVisible) return;

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.MODULE,
			{
				action: 'impression',
				nonInteraction: true,
			},
			[moduleContext.value]
		);
	},
}));

return { __sfc: true,props, tc, t, titles, genresByShortName, genreFullName, title, text, linkText, link, trackingLabel, moduleContext, handlePosterClick, handleSeeAllClick, observeVisibilityOptions, HiddenHorizontalScrollbar, PosterGraphql, TitleDetailLink }
}

})